// frontend/modules/customer-notification/components/history/NotificationHistoryTable.tsx
import React, { useState } from 'react';
import { Table, Tag, Badge, Space, Button, Tooltip, Drawer, Typography, Divider, Card, Descriptions, Empty } from 'antd';
import { 
    CheckCircleOutlined, 
    CloseCircleOutlined,
    WhatsAppOutlined,
    MailOutlined,
    MessageOutlined,
    EyeOutlined,
    UserOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import { CustomerNotificationHistory, NotificationPlatforms, NotificationCategoryType } from '@/shared/api/model';

const { Text, Title, Paragraph } = Typography;

interface NotificationHistoryTableProps {
    notifications: CustomerNotificationHistory[];
    isLoading: boolean;
}

const NotificationHistoryTable: React.FC<NotificationHistoryTableProps> = ({
    notifications,
    isLoading
}) => {
    const [selectedNotification, setSelectedNotification] = useState<CustomerNotificationHistory | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    
    const viewNotificationDetails = (notification: CustomerNotificationHistory) => {
        setSelectedNotification(notification);
        setDrawerVisible(true);
    };
    
    const closeDrawer = () => {
        setDrawerVisible(false);
    };
    
    const getPlatformTag = (platform: NotificationPlatforms) => {
        switch (platform) {
            case NotificationPlatforms.SMS:
                return (
                    <Tag icon={<MessageOutlined />} color="blue">
                        SMS
                    </Tag>
                );
                
            case NotificationPlatforms.WHATS_APP:
                return (
                    <Tag icon={<WhatsAppOutlined />} color="green">
                        WhatsApp
                    </Tag>
                );
                
            case NotificationPlatforms.EMAIL:
                return (
                    <Tag icon={<MailOutlined />} color="purple">
                        Email
                    </Tag>
                );
                
            default:
                return (
                    <Tag>
                        {platform}
                    </Tag>
                );
        }
    };
    
    const getCategoryTag = (category: NotificationCategoryType) => {
        const categoryColors: Record<string, string> = {
            LOAN_REMINDER: 'green',
            LOAN_OVERDUE: 'red',
            GENERAL_MEETING: 'blue',
            PRODUCT_CHANGE: 'purple',
            INTEREST_RATE_CHANGE: 'volcano',
            PROMOTIONAL: 'orange',
            SYSTEM_ALERT: 'magenta',
            ACCOUNT_ACTIVITY: 'cyan',
            GENERAL: 'default'
        };
        
        return (
            <Tag color={categoryColors[category] || 'default'}>
                {formatCategoryName(category)}
            </Tag>
        );
    };
    
    const columns = [
        {
            title: 'Timestamp',
            dataIndex: 'sentAt',
            key: 'sentAt',
            render: (sentAt: string) => (
                <Space direction="vertical" size={0}>
                    <Text className="text-xs">
                        {new Date(sentAt).toLocaleDateString()}
                    </Text>
                    <Text className="text-xs text-gray-500">
                        {new Date(sentAt).toLocaleTimeString()}
                    </Text>
                </Space>
            ),
            sorter: (a: CustomerNotificationHistory, b: CustomerNotificationHistory) =>
                // @ts-ignore
                new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime(),
            defaultSortOrder: 'descend'
        },
        {
            title: 'Status',
            dataIndex: 'success',
            key: 'success',
            render: (success: boolean, record: CustomerNotificationHistory) => (
                <Badge 
                    status={success ? "success" : "error"} 
                    text={
                        <Space>
                            {success ? "Delivered" : "Failed"}
                            {record.deliveredAt && (
                                <Tooltip title={`Delivered at ${new Date(record.deliveredAt).toLocaleString()}`}>
                                    <ClockCircleOutlined />
                                </Tooltip>
                            )}
                        </Space>
                    }
                />
            ),
            filters: [
                { text: 'Delivered', value: true },
                { text: 'Failed', value: false }
            ],
            onFilter: (value: boolean, record: CustomerNotificationHistory) => record.success === value
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (_:any, record: CustomerNotificationHistory) => (
                <Tooltip title={`Branch: ${record.branch}`}>
                    <Text>
                        {record.customerNumber}
                    </Text>
                </Tooltip>
            )
        },
        {
            title: 'Recipient',
            dataIndex: 'recipient',
            key: 'recipient',
            render: (recipient: string) => (
                <Text ellipsis style={{ maxWidth: 150 }}>
                    {recipient}
                </Text>
            )
        },
        {
            title: 'Template',
            dataIndex: 'templateName',
            key: 'templateName',
            render: (templateName: string) => (
                <Text ellipsis style={{ maxWidth: 180 }}>
                    {templateName}
                </Text>
            )
        },
        {
            title: 'Platform',
            dataIndex: 'platform',
            key: 'platform',
            render: (platform: NotificationPlatforms) => getPlatformTag(platform),
            filters: [
                { text: 'SMS', value: NotificationPlatforms.SMS },
                { text: 'WhatsApp', value: NotificationPlatforms.WHATS_APP },
                { text: 'Email', value: NotificationPlatforms.EMAIL }
            ],
            onFilter: (value: string, record: CustomerNotificationHistory) => record.platform === value
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category: NotificationCategoryType) => getCategoryTag(category),
            filters: Object.values(NotificationCategoryType).map(category => ({
                text: formatCategoryName(category),
                value: category
            })),
            onFilter: (value: string, record: CustomerNotificationHistory) => record.category === value
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_:any, record: CustomerNotificationHistory) => (
                <Button 
                    icon={<EyeOutlined />} 
                    size="small"
                    onClick={() => viewNotificationDetails(record)}
                >
                    View
                </Button>
            )
        }
    ];
    
    return (
        <>
            <Table 
                dataSource={notifications}
                // @ts-ignore
                columns={columns} 
                rowKey="id" 
                loading={isLoading}
                pagination={{ 
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => `Total ${total} items`
                }}
                locale={{
                    emptyText: <Empty description="No notification history found" />
                }}
            />
            
            <Drawer
                title="Notification Details"
                placement="right"
                closable={true}
                onClose={closeDrawer}
                open={drawerVisible}
                width={520}
            >
                {selectedNotification && (
                    <div>
                        <Descriptions 
                            title="Basic Information" 
                            bordered 
                            column={1}
                            className="mb-4"
                        >
                            <Descriptions.Item label="Status">
                                <Badge 
                                    status={selectedNotification.success ? "success" : "error"} 
                                    text={selectedNotification.success ? "Delivered" : "Failed"} 
                                />
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Notification ID">
                                <Text copyable>{selectedNotification.id}</Text>
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Sent At">
                                <Space>
                                    <CalendarOutlined />
                                    <Text>{
                                        // @ts-ignore
                                        new Date(selectedNotification.sentAt).toLocaleString()}</Text>
                                </Space>
                            </Descriptions.Item>
                            
                            {selectedNotification.deliveredAt && (
                                <Descriptions.Item label="Delivered At">
                                    <Space>
                                        <ClockCircleOutlined />
                                        <Text>{new Date(selectedNotification.deliveredAt).toLocaleString()}</Text>
                                    </Space>
                                </Descriptions.Item>
                            )}
                            
                            <Descriptions.Item label="Template">
                                <Text>{selectedNotification.templateName}</Text>
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Platform">
                                {
                                    // @ts-ignore
                                    getPlatformTag(selectedNotification.platform)}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Category">
                                {
                                    // @ts-ignore
                                    getCategoryTag(selectedNotification.category)}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Language">
                                <Tag>{selectedNotification.language}</Tag>
                            </Descriptions.Item>
                        </Descriptions>
                        
                        <Descriptions 
                            title="Recipient Information" 
                            bordered 
                            column={1}
                            className="mb-4"
                        >
                            <Descriptions.Item label="Customer">
                                <Space>
                                    <UserOutlined />
                                    <Text>{selectedNotification.customerNumber}</Text>
                                </Space>
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Branch">
                                {selectedNotification.branch}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Recipient">
                                <Text copyable>{selectedNotification.recipient}</Text>
                            </Descriptions.Item>
                            
                            {selectedNotification.loanId && (
                                <Descriptions.Item label="Loan ID">
                                    <Text>{selectedNotification.loanId}</Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                        
                        <Card title="Message Content" className="mb-4">
                            <Paragraph className="whitespace-pre-wrap">
                                {selectedNotification.messageContent || 'No content available'}
                            </Paragraph>
                        </Card>
                        
                        {selectedNotification.messageParameters && Object.keys(selectedNotification.messageParameters).length > 0 && (
                            <Card title="Template Parameters">
                                <div className="max-h-60 overflow-y-auto">
                                    {Object.entries(selectedNotification.messageParameters).map(([key, value]) => (
                                        <div key={key} className="mb-2">
                                            <Text strong>{key}: </Text>
                                            <Text>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Text>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}
                        
                        {selectedNotification.errorMessage && (
                            <Card 
                                title="Error Details" 
                                className="mt-4" 
                                headStyle={{ backgroundColor: '#fff1f0', color: '#cf1322' }}
                                bodyStyle={{ backgroundColor: '#fff2f0' }}
                            >
                                <Paragraph type="danger">
                                    {selectedNotification.errorMessage}
                                </Paragraph>
                            </Card>
                        )}
                    </div>
                )}
            </Drawer>
        </>
    );
};

// Helper function to format category names
const formatCategoryName = (category: string): string => {
    return category
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

export default NotificationHistoryTable;